// ProductBatchPanel — shows a product's current batches (qty, expiry, bin, status) so the operator
// is aware of what's already in stock while receiving. Reused across scan / manual / verify / OCR.
import type { ProductBatchInfo } from './api';

const STATUS_STYLE: Record<
  ProductBatchInfo['status'],
  { label: string; color: string; bg: string }
> = {
  ok: { label: 'In date', color: 'var(--success-dark)', bg: 'var(--success-soft)' },
  expiring: { label: 'Short expiry', color: 'var(--warning-dark)', bg: 'var(--warning-soft)' },
  expired: { label: 'Expired', color: 'var(--error-dark)', bg: 'var(--error-soft)' },
};

export function ProductBatchPanel({
  batches,
  title = 'Batches already in stock',
}: {
  batches: ProductBatchInfo[];
  title?: string;
}) {
  const live = batches.filter((b) => b.qty_on_hand > 0);
  return (
    <div
      style={{
        border: '1px solid var(--divider)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        background: 'var(--bg-card)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: 'var(--grey-100)',
          fontSize: 'var(--fs-body-sm)',
          fontWeight: 700,
          color: 'var(--text-secondary)',
        }}
      >
        <span>{title}</span>
        <span>
          {live.length} live batch{live.length === 1 ? '' : 'es'}
        </span>
      </div>
      {batches.length === 0 ? (
        <div
          style={{
            padding: '10px 12px',
            fontSize: 'var(--fs-body-sm)',
            color: 'var(--text-secondary)',
          }}
        >
          No stock on hand for this product yet — this will be the first batch.
        </div>
      ) : (
        <div style={{ maxHeight: 168, overflowY: 'auto' }}>
          {batches.map((b, i) => {
            const s = STATUS_STYLE[b.status];
            return (
              <div
                key={`${b.batch_number}-${b.bin_location}-${i}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.4fr 1fr 0.9fr auto',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: '7px 12px',
                  borderTop: i === 0 ? 'none' : '1px solid var(--divider)',
                  fontSize: 'var(--fs-body-sm)',
                  opacity: b.qty_on_hand > 0 ? 1 : 0.55,
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={b.batch_number}
                >
                  {b.batch_number || '—'}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {b.qty_on_hand} on hand{b.bin_location ? ` · ${b.bin_location}` : ''}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {b.expiry_date ? `Exp ${b.expiry_date}` : 'No expiry'}
                </span>
                <span
                  style={{
                    justifySelf: 'end',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-pill)',
                    fontSize: 'var(--fs-caption)',
                    fontWeight: 700,
                    color: s.color,
                    background: s.bg,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
